import { EMapProvider, ICommonSettings, IImageLibraryDto, ILanguageModel, IWebConfig } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { PureComponent, ReactNode } from "react";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "./app-settings-config-container";
import { FavIconField } from "./app-settings-config-favicon/app-settings-config-favicon-input";
import { ConfigFrontendUrlInput } from "./app-settings-config-frontend-url/app-settings-config-frontend-url-input";
import { ConfigAMapInput } from "./app-settings-config-maps/app-settings-config-amap-input";
import { ConfigGoogleMapsInput } from "./app-settings-config-maps/app-settings-config-google-maps-input";
import { ConfigMapProviderSelect } from "./app-settings-config-maps/app-settings-config-map-provider-select";
import { ConfigPushNotificationInput } from "./app-settings-config-push-notification/app-settings-config-push-notification-input";
import { ConfigSessionInput } from "./app-settings-config-session/app-settings-config-session-input";
import { ConfigTwinInput } from "./app-settings-config-twin/app-settings-config-twin-input";
import { IConfigCategory, visibleCategories, WEB_CATEGORIES } from "./config-categories";
import { ConfigField, ConfigSection, ConfigSupportToggle } from "./config-pane-parts";
import {
  ConfigCategoryStatus,
  ConfigDifference,
  ConfigPaneShell,
  ConfigRestorableValue,
  getOverriddenCategoryIds,
  readCategoryInheritance,
  restoreCategory
} from "./config-pane-shell";

export interface IWebConfigProps {
  /**
   * The container's whole parent/child difference, not just this pane's slice: a
   * category can own a key that lives on another one, and Restore writes it by its
   * full path. The rows below read `parentChildDifferences`, which narrows it.
   */
  parentChildDifference?: ConfigDifference;
  /** Form paths edited since the configuration was opened — the rail's dots and the orange labels. */
  changedPaths?: ReadonlySet<string>;
  /** Puts the named paths back to what they were when the configuration was opened. */
  onRevertPaths?: (paths: string[]) => void;
  /** True only when this configuration inherits from a workspace one. */
  hasParentConfig?: boolean;
  isGlobalConfig?: boolean;
  allImage?: IImageLibraryDto[];
  handleGetImageLibrary: () => void;
  languages: ILanguageModel[];
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleUpdateCustomFormInput: (formItemName: string[], value: ConfigRestorableValue) => void;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
  isSupporting: boolean;
  toggleSupport: () => void;
}

export interface IWebConfigState {
  /** The rail's selection. Pane-local, so it survives a trip through another platform tab. */
  activeCategoryId: string;
}

interface IWebSection {
  description?: ReactNode;
  titleTestId?: string;
  descriptionTestId?: string;
  body: ReactNode;
}

export class WebConfig extends PureComponent<IWebConfigProps, IWebConfigState> {
  constructor(props: IWebConfigProps) {
    super(props);

    this.state = { activeCategoryId: WEB_CATEGORIES[0].id };
  }

  /**
   * The categories this pane is drawing. The workspace surface has no host settings —
   * where a generated frontend is served from is a project's answer, not a workspace's.
   */
  getCategories = (): IConfigCategory[] =>
    visibleCategories(WEB_CATEGORIES, this.props.isGlobalConfig ? ["hostSettings"] : []);

  /**
   * The selection, corrected for a category that has gone away under it — switching to the
   * workspace surface takes Host Settings out from under a rail that was pointing at it.
   */
  getActiveCategory = (): IConfigCategory => {
    const categories: IConfigCategory[] = this.getCategories();
    const selected: IConfigCategory | undefined = categories.find(
      (category: IConfigCategory): boolean => category.id === this.state.activeCategoryId
    );
    return selected ?? categories[0];
  };

  handleSelectCategory = (categoryId: string): void => {
    this.setState({ activeCategoryId: categoryId });
  };

  /**
   * This pane's own slice of the difference — what the per-row restore affordances read.
   * A row only ever overrides a key on the slice it writes to.
   */
  get parentChildDifferences(): IWebConfig | undefined {
    return this.props.parentChildDifference?.webConfig as IWebConfig | undefined;
  }

  handleRestoreCategory = (): void => {
    restoreCategory(
      this.getActiveCategory(),
      this.props.parentChildDifference,
      "webConfig",
      this.props.handleUpdateCustomFormInput
    );
  };

  /** What each category puts in the middle column. Keyed by the registry's ids. */
  getSections = (): Record<string, IWebSection> => ({
    hostSettings: {
      description: this.props.getSectionDescription("web_hostsettings_description"),
      titleTestId: "hostSettingsTitle",
      descriptionTestId: "hostSettingsDescription",
      body: (
        <ConfigFrontendUrlInput
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    sessions: {
      description: this.props.getSectionDescription("web_sessions_description"),
      titleTestId: "sessionsTitle",
      descriptionTestId: "sessionsDescription",
      body: (
        <ConfigSessionInput
          parentConfigValue={this.parentChildDifferences?.sessionSettingsId}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    webMaps: {
      description: this.props.getSectionDescription("web_googlemaps_description"),
      titleTestId: "webGoogleMapsTitle",
      descriptionTestId: "webGoogleMapsDescription",
      body: (
        <ConfigMapProviderSelect
          parentData="webConfig"
          tooltipContentKey="web_maps_provider_info"
          supportsEndUserChoice
          renderProvider={(provider: EMapProvider) =>
            provider === EMapProvider.Amap ? (
              <ConfigAMapInput
                parentData="webConfig"
                parentConfigValue={this.parentChildDifferences?.amapSettingsId}
                commonSettings={this.props.commonSettings}
                getSectionDescription={this.props.getSectionDescription}
                handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
                handleCreateCommonConfig={this.props.handleCreateCommonConfig}
                handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
                handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
              />
            ) : (
              <ConfigGoogleMapsInput
                parentData="webConfig"
                parentConfigValue={this.parentChildDifferences?.googleMapsSettingsId}
                commonSettings={this.props.commonSettings}
                getSectionDescription={this.props.getSectionDescription}
                handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
                handleCreateCommonConfig={this.props.handleCreateCommonConfig}
                handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
                handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
              />
            )
          }
        />
      )
    },
    webPushNotification: {
      description: this.props.getSectionDescription("android_pushnotification_description"),
      body: (
        <ConfigPushNotificationInput
          parentData="webConfig"
          parentConfigValue={this.parentChildDifferences?.pushNotificationSettingsId}
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleUpdateCustomFormInput={this.props.handleUpdateCustomFormInput}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    },
    kuikagentsIntegration: {
      description:
        "Embed KuikAgents live dashboards, authenticated per tenant. Pick a KuikAgents Integration configuration or add a new one.",
      body: (
        <ConfigTwinInput
          commonSettings={this.props.commonSettings}
          getSectionDescription={this.props.getSectionDescription}
          handleCreateCommonConfig={this.props.handleCreateCommonConfig}
          handleUpdateCommonConfig={this.props.handleUpdateCommonConfig}
          handleDeleteCommonConfig={this.props.handleDeleteCommonConfig}
        />
      )
    }
  });

  /**
   * The pane's own column: the platform switch, where the selected category's values come
   * from, and the settings that belong to the web app rather than to any one category.
   */
  renderAside = (): ReactNode => {
    const { isGlobalConfig } = this.props;

    return (
      <>
        {!isGlobalConfig && (
          <ConfigSupportToggle
            label="Support Web UI"
            description={this.props.getSectionDescription("web_supportwebui_description")}
            checked={this.props.isSupporting}
            onChange={this.props.toggleSupport}
            switchTestId="supportWebSwitch"
            descriptionTestId="supportWebDescription"
            aside
          />
        )}

        {this.props.hasParentConfig && (
          <ConfigCategoryStatus
            inheritance={readCategoryInheritance(
              this.getActiveCategory(),
              this.props.parentChildDifference,
              "webConfig"
            )}
            onRestore={this.handleRestoreCategory}
          />
        )}

        {!isGlobalConfig && (
          <>
            <ConfigSection rail title="Title" titleTestId="webAppTitle">
              <ConfigField aside tooltipContentKey="web_title_title">
                <Form.Item name={["webConfig", "title"]}>
                  <KuikaInput data-testid="webAppTitleInput" size="md" placeholder="Kuika" />
                </Form.Item>
              </ConfigField>
            </ConfigSection>

            <ConfigSection
              rail
              title="Meta Description"
              titleTestId="webMetaDescriptionTitle"
              description={this.props.getSectionDescription("web_metadescription_description")}
              descriptionTestId="webMetaDescriptionDescription"
            >
              <ConfigField aside tooltipContentKey="web_metadescription_title">
                <Form.Item name={["webConfig", "metaDescription"]}>
                  {/* The one field with a character budget that matters — 160 is what a
                      search engine shows — so the counter is the point of it. */}
                  <KuikaInput
                    type="textarea"
                    size="md"
                    data-testid="webMetaDescriptionInput"
                    placeholder="Enter meta description for search engines"
                    rows={3}
                    maxLength={160}
                    showCount
                  />
                </Form.Item>
              </ConfigField>
            </ConfigSection>

            <ConfigSection
              rail
              title="Favicon"
              titleTestId="webFaviconTitle"
              description="32x32 px and JPG, JPEG, PNG or ICO file"
            >
              <ConfigField aside label="Image">
                <Form.Item name={["webConfig", "appIcon"]} noStyle>
                  <FavIconField
                    acceptTpye=".jpg,.jpeg,.png,.ico"
                    allImage={this.props.allImage}
                    handleGetImageLibrary={() => {
                      this.props.handleGetImageLibrary();
                    }}
                  />
                </Form.Item>
              </ConfigField>
            </ConfigSection>

            <ConfigSection
              rail
              title="PWA Install"
              titleTestId="webPwaTitle"
              description="Allow users to install the app on their device"
              descriptionTestId="webPwaDescription"
            >
              <Form.Item name={["webConfig", "enablePwa"]} valuePropName="checked" noStyle>
                <KuikaSwitch label="Enable installable PWA" dataTestId="webPwaSwitch" />
              </Form.Item>
            </ConfigSection>
          </>
        )}
      </>
    );
  };

  render() {
    const categories: IConfigCategory[] = this.getCategories();
    const sections: Record<string, IWebSection> = this.getSections();
    const activeCategoryId: string = this.getActiveCategory().id;

    return (
      <ConfigPaneShell
        changedPaths={this.props.changedPaths}
        onRevertPaths={this.props.onRevertPaths}
        categories={categories}
        activeCategoryId={activeCategoryId}
        onSelectCategory={this.handleSelectCategory}
        overriddenCategoryIds={getOverriddenCategoryIds(categories, this.props.parentChildDifference, "webConfig")}
        bodyVisible={this.props.isSupporting}
        aside={this.renderAside()}
      >
        {/* Every category stays mounted; `ConfigSection` hides the ones the rail is not
            pointing at. One antd Form spans all of them and save reads `getFieldsValue()`,
            so a category taken out of the tree is a field written back empty. */}
        {categories.map((category: IConfigCategory) => (
          <ConfigSection
            key={category.id}
            categoryId={category.id}
            active={category.id === activeCategoryId}
            title={category.label}
            description={sections[category.id]?.description}
            titleTestId={sections[category.id]?.titleTestId}
            descriptionTestId={sections[category.id]?.descriptionTestId}
          >
            {sections[category.id]?.body}
          </ConfigSection>
        ))}
      </ConfigPaneShell>
    );
  }
}
